import * as React from 'react';
import { ReferenceChildrenProps } from 'react-popper';
import { Placement as TypePlacement } from 'popper.js';
import { Props as BaseProps } from '../Base/Base';
import Placement from '../Placement/Placement';
import PlacementArea from '../Placement/PlacementArea';
import PlacementManager from '../Placement/PlacementManager';
import PlacementReference from '../Placement/PlacementReference';
import Text from '../Text/Text';
import { transitionDurationFast } from '../variables';

const { useState } = React;

interface TooltipChildrenProps extends ReferenceChildrenProps {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export interface Props extends BaseProps {
  /** Render callback that passes the ref and event handlers onto a target */
  children: (props: TooltipChildrenProps) => React.ReactNode;
  /** The content to be inserted into the tooltip. */
  title: string;
  /** Flag that controls the visibility of the tooltip */
  visible?: boolean;
  /** The desired animation speed on milliseconds. */
  timeout?: number;
  /** The desired placement direction */
  placement?: TypePlacement;
}

/**
 * A simple tooltip component that shows textual content above
 * a target when the mouse is over it.
 */
const Tooltip: React.FunctionComponent<Props> = (props: Props) => {
  const {
    backgroundColor = 'black',
    borderRadius = 'x1',
    children,
    placement = 'top',
    textColor = 'gray-1',
    title,
    visible: controlledVisible,
    timeout = transitionDurationFast,
    ...rest
  } = props;
  const [uncontrolledVisible, setVisible] = useState(controlledVisible || false);
  const isControlled = controlledVisible !== undefined;

  const handleMouseEnter = () => setVisible(true);
  const handleMouseLeave = () => setVisible(false);

  return (
    <PlacementManager>
      <Placement
          backgroundColor={ backgroundColor }
          borderRadius={ borderRadius }
          placement={ placement }
          textColor={ textColor }
          timeout={ timeout }
          { ...rest }
          visible={ isControlled ? controlledVisible : uncontrolledVisible }>
        { () => (
          <PlacementArea paddingHorizontal="x3" paddingVertical="x2" withArrow>
            <Text size="x2">{ title }</Text>
          </PlacementArea>
        ) }
      </Placement>

      <PlacementReference>
        { (props) => children({
          ...props,
          onMouseEnter: isControlled ? undefined : handleMouseEnter,
          onMouseLeave: isControlled ? undefined : handleMouseLeave,
        }) }
      </PlacementReference>
    </PlacementManager>
  );
};

export default Tooltip;
